import { useEffect, useRef, useState } from "react";
import { X, Circle, Square, RotateCcw, Check, SwitchCamera } from "lucide-react";
import { toast } from "sonner";

const MAX_SECONDS = 60;

export default function VideoRecorder({ onComplete, onClose, label = "Record Video" }) {
  const liveRef = useRef(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const timerRef = useRef(null);
  const [facing, setFacing] = useState("user");
  const [recording, setRecording] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const [recordedUrl, setRecordedUrl] = useState(null);
  const [recordedBlob, setRecordedBlob] = useState(null);
  const [ready, setReady] = useState(false);

  const stopStream = () => { streamRef.current?.getTracks().forEach((t) => t.stop()); streamRef.current = null; };

  const startCamera = async (mode) => {
    stopStream();
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: mode }, audio: true });
      streamRef.current = stream;
      if (liveRef.current) { liveRef.current.srcObject = stream; await liveRef.current.play().catch(() => {}); }
      setReady(true);
    } catch (e) {
      toast.error("Camera/microphone access denied.");
      onClose?.();
    }
  };

  useEffect(() => { startCamera(facing); return () => { clearInterval(timerRef.current); stopStream(); }; }, []);

  const switchCamera = async () => { const next = facing === "user" ? "environment" : "user"; setFacing(next); await startCamera(next); };

  const start = () => {
    if (!streamRef.current) return;
    chunksRef.current = [];
    const mime = MediaRecorder.isTypeSupported("video/webm;codecs=vp9") ? "video/webm;codecs=vp9" : "video/webm";
    const mr = new MediaRecorder(streamRef.current, { mimeType: mime });
    mr.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
    mr.onstop = () => {
      const blob = new Blob(chunksRef.current, { type: "video/webm" });
      setRecordedBlob(blob);
      setRecordedUrl(URL.createObjectURL(blob));
    };
    mr.start();
    recorderRef.current = mr;
    setRecording(true);
    setSeconds(0);
    timerRef.current = setInterval(() => setSeconds((s) => { if (s + 1 >= MAX_SECONDS) stop(); return s + 1; }), 1000);
  };

  const stop = () => {
    clearInterval(timerRef.current);
    if (recorderRef.current && recorderRef.current.state !== "inactive") recorderRef.current.stop();
    setRecording(false);
  };

  const retake = () => { setRecordedUrl(null); setRecordedBlob(null); setSeconds(0); startCamera(facing); };

  const use = async () => {
    if (!recordedBlob) return;
    if (recordedBlob.size > 8 * 1024 * 1024) { toast.error("Clip too large (max 8MB). Record a shorter clip."); return; }
    const b64 = await new Promise((res) => { const r = new FileReader(); r.onload = () => res(r.result); r.readAsDataURL(recordedBlob); });
    stopStream();
    onComplete(b64);
  };

  const close = () => { stopStream(); onClose?.(); };

  return (
    <div className="fixed inset-0 bg-black/85 flex items-center justify-center p-4 z-[70]">
      <div className="card-surface rounded-2xl w-full max-w-md overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-3 border-b border-border">
          <span className="font-heading text-xl">{label}</span>
          <button data-testid="vr-close" onClick={close} className="w-8 h-8 rounded-full bg-white/5 flex items-center justify-center"><X className="w-4 h-4" /></button>
        </div>
        <div className="relative bg-black aspect-video">
          {recordedUrl ? (
            <video data-testid="vr-playback" src={recordedUrl} controls playsInline className="w-full h-full object-contain" />
          ) : (
            <video ref={liveRef} muted playsInline className="w-full h-full object-cover" />
          )}
          {recording && (
            <div className="absolute top-3 left-3 flex items-center gap-2 bg-black/60 px-2.5 py-1 rounded-full">
              <span className="w-2.5 h-2.5 rounded-full bg-brand animate-pulse" />
              <span className="font-num text-sm text-white">{String(Math.floor(seconds / 60)).padStart(2, "0")}:{String(seconds % 60).padStart(2, "0")}</span>
            </div>
          )}
          {!recordedUrl && !recording && ready && (
            <button data-testid="vr-switch" onClick={switchCamera} className="absolute top-3 right-3 w-9 h-9 rounded-full bg-black/60 flex items-center justify-center text-white"><SwitchCamera className="w-4 h-4" /></button>
          )}
        </div>
        <div className="p-4 flex items-center justify-center gap-3">
          {recordedUrl ? (
            <>
              <button data-testid="vr-retake" onClick={retake} className="px-5 py-2.5 border border-white/20 rounded-lg font-semibold text-sm flex items-center gap-2 hover:border-white"><RotateCcw className="w-4 h-4" /> Retake</button>
              <button data-testid="vr-use" onClick={use} className="px-6 py-2.5 bg-brand text-white rounded-lg font-bold uppercase text-sm tracking-wide flex items-center gap-2 hover:bg-brand-hover"><Check className="w-4 h-4" /> Use Clip</button>
            </>
          ) : recording ? (
            <button data-testid="vr-stop" onClick={stop} className="w-16 h-16 rounded-full bg-brand flex items-center justify-center glow-red"><Square className="w-6 h-6 text-white" /></button>
          ) : (
            <button data-testid="vr-start" disabled={!ready} onClick={start} className="w-16 h-16 rounded-full border-4 border-white/30 flex items-center justify-center disabled:opacity-50"><Circle className="w-10 h-10 text-brand fill-brand" /></button>
          )}
        </div>
        <p className="text-center text-tertiary text-xs pb-3">Max {MAX_SECONDS}s · up to 8MB</p>
      </div>
    </div>
  );
}
